import React, {useEffect, useRef, useState} from 'react';
import {Button, Form, Input, message, Modal, Select, Space, Tree} from 'antd';
import {DownOutlined, HomeOutlined, PlusOutlined} from '@ant-design/icons';
import type {DataNode, TreeProps} from 'antd/es/tree';
import {gql, useMutation, useQuery} from '@apollo/client';
import {CREATE_HUB, CREATE_WAREHOUSE, DISABLE_SITE} from '../graphql/mutations/warehouseMutations';
import {WarehouseType} from 'graphql/types/WarehouseType';
import {PhoneInput} from 'components/PhoneInput';
import CascadingAddressSelector from 'components/CascadingAddressSelector';
import {SiteType} from 'graphql/types/SiteType';
import { useTranslation } from 'react-i18next';
import {GET_ALL_ACCESS_HUB_AND_WAREHOUSES_FOR_ROLE} from 'graphql/queries/getAllAccessHubAndWarehousesForRole';

const {Search} = Input;

interface SitesTreeProps {
    onSelect?: (key: string) => void;
    onAddWarehouse?: (hubKey: string, warehouseName: string) => void;
    onAddHub?: (hubName: string) => void;
    hubFilterId?: number | string;
    allowCreateHub?: boolean;
    allowCreateWarehouse?: boolean;
}

// Hub node title component
const HubTreeTitle = ({
                          hub,
                          onAddWarehouse,
                          addWarehouseTitle,
                          allowCreateWarehouse = true
                      }: {
    hub: WarehouseType;
    onAddWarehouse: (hubId: string) => void;
    addWarehouseTitle: string;
    allowCreateWarehouse?: boolean;
}) => (
    <div className="flex items-center justify-between gap-2 w-full min-w-0">
        <div className="flex items-center gap-2 min-w-0">
            <span className="font-medium truncate">{hub.name}</span>
            <span className="text-xs text-gray-500 shrink-0">({hub.sortingCode})</span>
        </div>
        {allowCreateWarehouse && (
            <Button
                type="text"
                size="small"
                icon={<PlusOutlined/>}
                className="shrink-0 text-purple-500 hover:text-purple-600 hover:bg-purple-50"
                onClick={(e) => {
                    e.stopPropagation();
                    onAddWarehouse(hub.id?.toString() || '');
                }}
                title={addWarehouseTitle}
            />
        )}
    </div>
);

// Warehouse node title component
const WarehouseTreeTitle = ({warehouse}: { warehouse: WarehouseType }) => (
    <div className="flex items-center gap-2 w-full min-w-0">
        <HomeOutlined className="shrink-0"/>
        <span className="truncate">{warehouse.name}</span>
        <span className="text-xs text-gray-500 shrink-0">({warehouse.sortingCode})</span>
    </div>
);

// GraphQL query hook
const useGetAllAccessHubAndWarehousesForRole = () => {
    const {data, refetch} = useQuery(GET_ALL_ACCESS_HUB_AND_WAREHOUSES_FOR_ROLE);
    return {
        allWarehouses: data?.warehouses || [],
        refetch
    };
};

// Site form component
const SiteForm = ({
                      form,
                      isHub,
                      parentSiteId,
                      allWarehouses
                  }: {
    form: any;
    isHub: boolean;
    parentSiteId?: number;
    allWarehouses?: any[];
}) => {
    // 获取父hub的sorting code
    const parentHubSortingCode = React.useMemo(() => {
        if (isHub || !parentSiteId || !allWarehouses) return '';
        const parentHub = allWarehouses.find((w: any) => w.id == parentSiteId && w.siteType === 1);
        return parentHub?.sortingCode || '';
    }, [isHub, parentSiteId, allWarehouses]);
    const { t } = useTranslation();
    useEffect(() => {
        // 总是设置这些值，确保表单状态正确
        form.setFieldsValue({
            parentSiteId: parentSiteId || 0,
            siteType: isHub ? 1 : 2
        });
    }, [form, isHub, parentSiteId]);


    return (
        <Form form={form} layout="vertical" preserve={false}>
            <Form.Item name="parentSiteId" hidden>
                <Input type="hidden"/>
            </Form.Item>

            <Space.Compact style={{width: '100%'}}>
                <Form.Item
                    name="name"
                    label={isHub ? t('central.sites.details.hubName') : t('central.sites.details.warehouseName')}
                    rules={[{required: true, message: t('central.sites.messages.pleaseEnterSiteName') as string}]}
                    style={{flex: 1}}
                >
                    <Input placeholder={isHub ? t('central.sites.placeholders.enterHubName') as string : t('central.sites.placeholders.enterWarehouseName') as string}/>
                </Form.Item>

                <Form.Item
                    name="siteType"
                    label={t('central.sites.details.siteType')}
                    rules={[{required: true, message: t('central.sites.messages.pleaseSelectSiteType') as string}]}
                    style={{flex: 1}}
                >
                    <Select disabled>
                        <Select.Option value={1}>Hub</Select.Option>
                        <Select.Option value={2}>Warehouse</Select.Option>
                    </Select>
                </Form.Item>

                {isHub && (
                    <Form.Item
                        name="businessType"
                        label={t('central.sites.details.businessType')}
                        rules={[{required: true, message: t('central.sites.messages.pleaseSelectBusinessType') as string}]}
                        style={{flex: 1}}
                    >
                        <Select placeholder={t('central.sites.placeholders.selectBusinessType') as string}>
                            <Select.Option value={1}>{t('central.sites.details.businessTypeSorting')}</Select.Option>
                            <Select.Option value={2}>{t('central.sites.details.businessTypeLastMile')}</Select.Option>
                        </Select>
                    </Form.Item>
                )}
            </Space.Compact>

            <Space.Compact style={{width: '100%'}}>
                <Form.Item
                    name="phone"
                    label={t('central.sites.details.contactPhone')}
                    validateTrigger={['onChange', 'onBlur']}
                    rules={[
                        {
                            validator: (_, value) => {
                                // 如果phoneNumber为空，返回错误
                                if (!value?.phoneNumber || value.phoneNumber.trim() === '') {
                                    return Promise.reject(new Error(t('central.sites.messages.pleaseEnterPhoneNumber') as string));
                                }
                                if (!/^\d*$/.test(value.phoneNumber)) {
                                    return Promise.reject(new Error(t('central.sites.messages.onlyNumbersAllowed') as string));
                                }
                                if (value.phoneNumber.length < 7 || value.phoneNumber.length > 12) {
                                    return Promise.reject(new Error(t('central.sites.messages.phoneNumberMustBe7To12Digits') as string));
                                }
                                return Promise.resolve();
                            }
                        }
                    ]}
                    style={{flex: 1}}
                >
                    <PhoneInput/>
                </Form.Item>

                <Form.Item
                    name="extension"
                   label={t('central.sites.details.ext')}
                    rules={[
                        {pattern: /^\d*$/, message: t('central.sites.messages.onlyNumbersAllowed') as string},
                        {
                            validator: (_, value) => {
                                if (value && value.length > 10) {
                                    return Promise.reject(new Error('Extension number is too long, maximum 10 characters'));
                                }
                                return Promise.resolve();
                            }
                        }
                    ]}
                    style={{width: 120}}
                >
                    <Input
                        placeholder={t('central.sites.placeholders.extension') || ''}
                        onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            form.setFieldValue('extension', value);
                        }}
                    />
                </Form.Item>
            </Space.Compact>

            <Space.Compact style={{width: '100%'}}>
                <Form.Item
                    name="contactName"
                    label={t('central.sites.details.contactName')}
                    rules={[{required: true, message: t('central.sites.messages.pleaseEnterContactName') as string}]}
                    style={{flex: 1}}
                >
                    <Input placeholder={t('central.sites.placeholders.enterContactName') as string}/>
                </Form.Item>

                <Form.Item
                    name="sortingCode"
                    label={t('central.sites.details.shortingCode')}
                    rules={[
                        {required: true, message: t('central.sites.messages.sortingCodeIsRequired') as string},
                        // Hub只能包含大写字母
                        ...(isHub ? [{
                            pattern: /^[A-Z]+$/,
                            message: 'Hub sorting code can only contain uppercase letters (A-Z)'
                        }] : []),
                        // Warehouse后缀只能包含大写字母
                        ...(isHub ? [] : [{
                            validator: (_: any, value: string) => {
                                if (!value) {
                                    return Promise.resolve();
                                }

                                const expectedPattern = new RegExp(`^[A-Z]+$`);
                                if (!expectedPattern.test(value)) {
                                    return Promise.reject(
                                        new Error(`Hub sorting code can only contain uppercase letters (A-Z)`)
                                    );
                                }

                                return Promise.resolve();
                            }
                        }])
                    ]}
                    style={{flex: 1}}
                >
                    {isHub ? (
                        <Input
                            placeholder={t('central.sites.placeholders.enterSortingCode') || ''}
                            onChange={(e) => {
                                const value = e.target.value;
                                form.setFieldValue('sortingCode',value);
                            }}
                        />
                    ) : (
                        <div className="flex items-center">
                            {parentHubSortingCode && (
                                <span className="text-gray-500 mr-1">{parentHubSortingCode}-</span>
                            )}
                            <Input
                                placeholder="Enter suffix (A-Z only)"
                                onChange={(e) => {
                                    // 只允许大写字母
                                    const suffix = e.target.value;
                                    const fullSortingCode = parentHubSortingCode ? `${parentHubSortingCode}-${suffix}` : suffix;
                                    form.setFieldValue('sortingCode', fullSortingCode);
                                }}
                                style={{flex: 1}}
                            />
                        </div>
                    )}
                </Form.Item>
            </Space.Compact>

            <Space.Compact style={{width: '100%'}}>
                <Form.Item
                    name="address"
                    label={t('central.sites.details.addr1')}
                    rules={[{required: true, message: t('central.sites.messages.pleaseEnterAddress') || ''} as any]}
                    style={{flex: 1}}
                >
                    <Input placeholder={t('central.sites.placeholders.enterAddress') as string}/>
                </Form.Item>

                <Form.Item
                    name="address2"
                    label={t('central.sites.details.addr2')}
                    style={{flex: 1}}
                >
                    <Input placeholder={t('central.sites.placeholders.enterAddress2') as string}/>
                </Form.Item>
            </Space.Compact>

            <Form.Item
                label={t('central.sites.details.address')}
                required
            >
                <CascadingAddressSelector
                    form={form}
                    countryWidth="20%"
                    stateWidth="20%"
                    cityWidth="30%"
                    zipcodeWidth="30%"
                    autoFillGeoLocation={true}
                    geoLocationFieldName="location"
                    countryPlaceholder={t('central.sites.placeholders.country') as string}
                    statePlaceholder={t('central.sites.placeholders.state') as string}
                    cityPlaceholder={t('central.sites.placeholders.city') as string}
                    zipcodePlaceholder={t('central.sites.placeholders.zipcode') as string}
                />
            </Form.Item>

            <Space.Compact style={{width: '100%'}}>
                <Form.Item
                    name="timezone"
                    label={t('central.sites.details.timezone')}
                    style={{flex: 1}}
                >
                    <Input
                        placeholder={t('central.sites.placeholders.autoFilledFromZipcode') as string}
                        disabled
                        style={{backgroundColor: '#f5f5f5'}}
                    />
                </Form.Item>

                <Form.Item
                    name="location"
                    label={t('central.sites.details.geoCoding')}
                    style={{flex: 1}}
                >
                    <Input
                        placeholder={t('central.sites.placeholders.autoFilledFromZipcode') as string}
                        disabled
                        style={{backgroundColor: '#f5f5f5'}}
                    />
                </Form.Item>
            </Space.Compact>
        </Form>
    );
};

const SitesTree: React.FC<SitesTreeProps> = ({
                                                 onSelect,
                                                 onAddWarehouse: propOnAddWarehouse,
                                                 onAddHub: propOnAddHub,
                                                 hubFilterId,
                                                 allowCreateHub = true,
                                                 allowCreateWarehouse = true
                                             }) => {
    const { t } = useTranslation();

    // GraphQL hooks
    const {allWarehouses, refetch: refetchWarehouses} = useGetAllAccessHubAndWarehousesForRole();
    const [createHubMutation] = useMutation(CREATE_HUB);
    const [createWarehouseMutation] = useMutation(CREATE_WAREHOUSE);
    const [disableSiteMutation] = useMutation(DISABLE_SITE);

    // State
    const [selectedKey, setSelectedKey] = useState<string>('');
    const [treeData, setTreeData] = useState<DataNode[]>([]);
    const [filteredTreeData, setFilteredTreeData] = useState<DataNode[]>([]);
    const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
    const [searchValue, setSearchValue] = useState<string>('');
    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState<'hub' | 'warehouse'>('warehouse');
    const [modalLoading, setModalLoading] = useState(false);
    const [parentSiteId, setParentSiteId] = useState<number>(0);
    const [form] = Form.useForm();

    // Build tree data
    useEffect(() => {
        if (allWarehouses?.length > 0) {
            // Tree 排序规则（用于 /central/settings/sites/management 左侧树）：
            // - Hub 按名称字母序排序（忽略大小写）
            // - Hub 下的 Warehouse 同样按名称字母序排序（忽略大小写）
            // - 兜底：name 为空时按 sortingCode，再按 id，保证排序稳定
            const compareByName = (a?: { name?: string; sortingCode?: string; id?: number }, b?: { name?: string; sortingCode?: string; id?: number }) => {
                const aName = (a?.name || '').trim();
                const bName = (b?.name || '').trim();
                if (aName && bName) {
                    const cmp = aName.localeCompare(bName, undefined, { sensitivity: 'base', numeric: true });
                    if (cmp !== 0) return cmp;
                } else if (aName || bName) {
                    return aName ? -1 : 1;
                }

                const aCode = (a?.sortingCode || '').trim();
                const bCode = (b?.sortingCode || '').trim();
                if (aCode && bCode) {
                    const cmp = aCode.localeCompare(bCode, undefined, { sensitivity: 'base', numeric: true });
                    if (cmp !== 0) return cmp;
                } else if (aCode || bCode) {
                    return aCode ? -1 : 1;
                }

                return (a?.id || 0) - (b?.id || 0);
            };

            const warehouses = allWarehouses.map((w: any) => ({
                ...w,
                id: Number(w.id),
                parentSiteId: Number(w.parentSiteId)
            }));

            // Filter warehouses by hubFilterId if provided
            let filteredWarehouses = warehouses;
            if (hubFilterId) {
                const hubIdNum = Number(hubFilterId);
                filteredWarehouses = warehouses.filter((w: any) => {
                    // Keep the specified Hub
                    if (w.siteType === SiteType.HUB && w.id === hubIdNum) {
                        return true;
                    }
                    // Keep warehouses under the specified Hub
                    if (w.siteType === SiteType.WAREHOUSE && w.parentSiteId === hubIdNum) {
                        return true;
                    }
                    return false;
                });
            }

            const hubMap = new Map<number, { hub: WarehouseType; warehouses: WarehouseType[] }>();

            // Group warehouses by hub
            filteredWarehouses.forEach((warehouse: any) => {
                if (warehouse.siteType === SiteType.HUB) {
                    hubMap.set(warehouse.id || 0, {hub: warehouse, warehouses: []});
                }
            });

            filteredWarehouses.forEach((warehouse: any) => {
                if (warehouse.siteType === SiteType.WAREHOUSE) {
                    const hub = hubMap.get(warehouse.parentSiteId || 0);
                    if (hub) {
                        hub.warehouses.push(warehouse);
                    }
                }
            });

            const sortedHubs = Array.from(hubMap.values()).sort((a, b) => compareByName(a.hub, b.hub));
            sortedHubs.forEach(group => {
                group.warehouses.sort((a, b) => compareByName(a, b));
            });

            const treeNodes: DataNode[] = sortedHubs.map(hub => ({
                key: hub.hub.id || '',
                title: (
                    <HubTreeTitle
                        hub={hub.hub}
                        onAddWarehouse={handleAddWarehouse}
                        addWarehouseTitle={t('central.sites.details.addWarehouse')}
                        allowCreateWarehouse={allowCreateWarehouse}
                    />
                ),
                isLeaf: false,
                // 添加搜索用的数据
                searchData: {
                    name: hub.hub.name || '',
                    sortingCode: hub.hub.sortingCode || ''
                },
                children: hub.warehouses.map(warehouse => ({
                    key: warehouse.id || '',
                    isLeaf: true,
                    title: <WarehouseTreeTitle warehouse={warehouse}/>,
                    // 添加搜索用的数据
                    searchData: {
                        name: warehouse.name || '',
                        sortingCode: warehouse.sortingCode || ''
                    }
                }))
            }));

            setTreeData(treeNodes);
        }
    }, [allWarehouses, hubFilterId, allowCreateWarehouse, t]);

    // Search filter effect
    useEffect(() => {
        const filtered = filterTreeData(treeData, searchValue);
        setFilteredTreeData(filtered);
    }, [treeData, searchValue]);

    // 父组件传入 hubFilterId 时（如 /wms/sites 由 WMS 左侧 sidebar 下拉决定），
    // 进入页面或切换下拉后，自动选中对应 Hub 并通知父组件加载右侧详情。
    // 用 ref 记住上一次值，仅在 hubFilterId 真正变化时触发，避免用户点子 Warehouse 后被强行回拉。
    const lastAppliedHubFilterIdRef = useRef<number | string | undefined>(undefined);
    useEffect(() => {
        if (hubFilterId == null) return;
        if (lastAppliedHubFilterIdRef.current === hubFilterId) return;
        lastAppliedHubFilterIdRef.current = hubFilterId;
        // tree node key 在构建时被转成了 number（见 treeData 构建处的 Number(w.id)），
        // 这里需要用 number 才能让 antd Tree 的 selectedKeys 严格匹配上节点。
        const hubKey = Number(hubFilterId) as unknown as string;
        setSelectedKey(hubKey);
        onSelect?.(String(hubFilterId));
    }, [hubFilterId, onSelect]);

    // Handlers
    const handleAddWarehouse = (hubId: string) => {
        setModalType('warehouse');
        setParentSiteId(parseInt(hubId));
        form.resetFields();
        // 确保 siteType 被正确设置
        form.setFieldsValue({
            parentSiteId: parseInt(hubId),
            siteType: 2 // Warehouse
        });
        setModalVisible(true);
    };

    const handleAddHub = () => {
        setModalType('hub');
        setParentSiteId(0);
        form.resetFields();
        // 确保 siteType 被正确设置
        form.setFieldsValue({
            parentSiteId: 0,
            siteType: 1 // Hub
        });
        setModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            setModalLoading(true);
            const values = await form.validateFields();

            // 手动验证地址字段
            if (!values.country) {
                form.setFields([{name: 'country', errors: [t('central.sites.messages.pleaseSelectCountry') as string]}]);
                return;
            }
            if (!values.state) {
                form.setFields([{name: 'state', errors: [t('central.sites.messages.pleaseSelectState') as string]}]);
                return;
            }
            if (!values.city) {
                form.setFields([{name: 'city', errors: [t('central.sites.messages.pleaseSelectCity') as string]}]);
                return;
            }
            if (!values.zipcode) {
                form.setFields([{name: 'zipcode', errors: [t('central.sites.messages.pleaseSelectZipcode') as string]}]);
                return;
            }

            // Parse location
            let location = undefined;
            if (values.location?.trim()) {
                const coords = values.location.split(',').map((coord: string) => parseFloat(coord.trim()));
                if (coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
                    location = {latitude: coords[0], longitude: coords[1]};
                }
            }

            // 确保warehouse的sortingCode包含正确的前缀
            let finalSortingCode = values.sortingCode;
            if (modalType === 'warehouse' && parentSiteId) {
                // 获取父hub的sortingCode
                const parentHub = allWarehouses.find((w: any) => w.id == parentSiteId && w.siteType === 1);
                const parentHubSortingCode = parentHub?.sortingCode || '';

                if (parentHubSortingCode) {
                    // 如果sortingCode不是以父hub前缀开头，则添加前缀
                    if (!finalSortingCode.startsWith(parentHubSortingCode + '-')) {
                        finalSortingCode = `${parentHubSortingCode}-${values.sortingCode}`;
                    }
                }
            }

            const warehouse: WarehouseType = {
                name: values.name,
                parentSiteId: values.parentSiteId,
                siteType: values.siteType,
                addressLine1: values.address || 'TBD',
                addressLine2: values.address2 || '',
                country: values.country || 'US',
                state: values.state || '',
                city: values.city || '',
                zipcode: values.zipcode || '',
                location,
                sortingCode: finalSortingCode,
                contactName: values.contactName,
                phoneNumber: values.phone?.phoneNumber || values.phoneNumber,
                countryCode: values.phone?.countryCode || 1,
                extension: values.extension || '',
                deliveryZoneZipcodeList: [],
                pickupZoneZipcodeList: [],
                timezone: values.timezone || 'America/New_York', // 确保 timezone 被设置
                businessType: values.businessType || undefined
            };

            // Execute mutation
            if (modalType === 'hub') {
                await createHubMutation({variables: {warehouse}});
                propOnAddHub?.(values.name);
            } else {
                await createWarehouseMutation({variables: {warehouse}});
                propOnAddWarehouse?.(values.parentSiteId, values.name);
            }

            refetchWarehouses();
            setModalVisible(false);
            form.resetFields();

        } catch (error: any) {
            console.error(`Failed to create ${modalType}:`, error);

            // Frontend validation error - keep modal open
            if (error?.errorFields || error?.name === 'ValidationError') {
                return;
            }

            // Backend error - keep modal open, show error message
            if (error?.graphQLErrors?.length) {
                const gqlErr = error.graphQLErrors[0];
                const messageKey = gqlErr.extensions?.messageKey;
                const errMsg = messageKey ? t(messageKey) : gqlErr.message;
                message.error(errMsg as string);
                return;
            }
            if (error?.networkError) {
                message.error(t('errors.system.networkError') as string);
                return;
            }
        } finally {
            setModalLoading(false);
        }
    };

    const handleCancel = () => {
        setModalVisible(false);
        form.resetFields();
    };

    const handleSelect: TreeProps['onSelect'] = (selectedKeys) => {
        if (selectedKeys.length > 0) {
            const key = selectedKeys[0] as string;
            setSelectedKey(key);
            onSelect?.(key);
        }
    };

    const handleSearch = (value: string) => {
        setSearchValue(value);
        if (value) {
            const keysToExpand: React.Key[] = [];
            treeData.forEach(node => {
                if (node.children?.length) {
                    keysToExpand.push(node.key);
                }
            });
            setExpandedKeys(keysToExpand);
        }
    };

    const filterTreeData = (data: DataNode[], searchText: string): DataNode[] => {
        if (!searchText) return data;

        const searchLower = searchText.toLowerCase();

        return data.map(node => {
            // 检查 Hub 是否匹配
            const hubSearchData = (node as any).searchData;
            const hubMatches = hubSearchData && (
                hubSearchData.name.toLowerCase().includes(searchLower) ||
                hubSearchData.sortingCode.toLowerCase().includes(searchLower)
            );

            // 过滤子节点（Warehouses）
            const filteredChildren = node.children?.filter(child => {
                const warehouseSearchData = (child as any).searchData;
                return warehouseSearchData && (
                    warehouseSearchData.name.toLowerCase().includes(searchLower) ||
                    warehouseSearchData.sortingCode.toLowerCase().includes(searchLower)
                );
            }) || [];

            // 如果 Hub 匹配或有匹配的子节点，则包含此节点
            if (hubMatches || filteredChildren.length > 0) {
                return {
                    ...node,
                    children: hubMatches ? node.children : filteredChildren
                };
            }

            return null;
        }).filter(Boolean) as DataNode[];
    };

    return (
        <div className="border-r border-gray-200 p-4 flex flex-col gap-2 w-[360px] shrink-0 h-full overflow-y-auto">
            <Search
                placeholder={t('central.sites.placeholders.searchHubWarehouse') as string}
                className="mb-4"
                allowClear
                value={searchValue}
                onChange={(e) => handleSearch(e.target.value)}
                onSearch={handleSearch}
            />

            <Tree
                showIcon
                expandedKeys={expandedKeys}
                onExpand={setExpandedKeys}
                selectedKeys={selectedKey ? [selectedKey] : []}
                switcherIcon={<DownOutlined/>}
                onSelect={handleSelect}
                treeData={searchValue ? filteredTreeData : treeData}
                className="w-full [&_.ant-tree-indent-unit]:w-4"
            />

            {allowCreateHub && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                    <Button
                        type="dashed"
                        icon={<PlusOutlined/>}
                        onClick={handleAddHub}
                        className="w-full text-purple-500 border-purple-300 hover:text-purple-600 hover:border-purple-400"
                    >
                        <span className="font-bold">{t('central.sites.details.addHub')}</span>
                    </Button>
                </div>
            )}

            <Modal
                title={modalType === 'hub' ? t('central.sites.details.addHub') : t('central.sites.details.addWarehouse')}
                open={modalVisible}
                onOk={handleSubmit}
                okText={t('components.button.ok')}
                onCancel={handleCancel}
                cancelText={t('components.button.cancel')}
                width={800}
                confirmLoading={modalLoading}
                destroyOnClose={false}
                maskClosable={false}
            >
                <SiteForm
                    form={form}
                    isHub={modalType === 'hub'}
                    parentSiteId={parentSiteId}
                    allWarehouses={allWarehouses}
                />
            </Modal>
        </div>
    );
};

export default SitesTree;
